import React, { useState } from "react";
import { DISTRIBUTORS } from "@/data/synthetic";

/*
Stylized geo-network map of US + Canada distributor territories.
Uses approximate normalized coordinates (not real geo projection) to plot
distributors on a hand-drawn continental outline. Pure SVG.
*/

// Approximate (x,y) coordinates in a 1000×520 viewBox — spread across a stylized North-America map
const COORDS = {
  "D-096": { x: 155, y: 305, city: "Los Angeles" },  // West
  "D-052": { x: 430, y: 415, city: "Houston" },      // Texas
  "D-041": { x: 555, y: 285, city: "Chicago" },      // Midwest
  "D-085": { x: 680, y: 380, city: "Atlanta" },      // Southeast
  "D-063": { x: 690, y: 225, city: "Toronto" },      // Ontario
  "D-107": { x: 790, y: 175, city: "Montréal" },     // Quebec
  "D-074": { x: 830, y: 250, city: "Boston" },       // Northeast
};

const HUB = { x: 555, y: 285, name: "NorthStar HQ · Chicago" };

const colorForScore = (s) => (s >= 75 ? "#22C55E" : s >= 65 ? "#00E5FF" : "#F97316");

export default function DistributorNetworkMap({ onSelect }) {
  const [hover, setHover] = useState(null);

  return (
    <div className="glass rounded-xl p-5 relative overflow-hidden" data-testid="distributor-network-map">
      <div className="flex items-center justify-between mb-3">
        <div className="tech-label">TERRITORY NETWORK · US · CA</div>
        <div className="flex items-center gap-3 text-[10px] font-mono text-slate-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /> HEALTHY</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-cyan-400" /> WATCH</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400" /> AT RISK</span>
        </div>
      </div>

      <div className="relative rounded-lg bg-black/30 border border-white/5 overflow-hidden">
        <svg viewBox="0 0 1000 520" className="w-full h-[440px]" preserveAspectRatio="xMidYMid meet">
          {/* Faint graticule */}
          <defs>
            <pattern id="grat" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgba(148,163,184,0.05)" strokeWidth="0.5" />
            </pattern>
            <radialGradient id="hubGlow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#00E5FF" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect width="1000" height="520" fill="url(#grat)" />

          {/* Stylized North America outline (simplified path). US + Canada silhouette. */}
          <g fill="none" stroke="rgba(148,163,184,0.35)" strokeWidth="1.4">
            {/* Canada rough top */}
            <path d="M60,110 C160,60 280,50 400,60 C520,50 640,60 760,80 C820,90 880,120 920,150" />
            {/* West coast US */}
            <path d="M110,180 L100,240 L130,300 L150,350 L175,420" />
            {/* US south / Gulf coast */}
            <path d="M175,420 C260,455 340,465 420,455 C500,450 560,470 620,455 C680,445 730,455 800,435" />
            {/* US east coast */}
            <path d="M800,435 C830,400 850,340 855,290 C860,240 850,190 815,150 C790,130 750,120 720,120" />
            {/* Canada east - Maritimes */}
            <path d="M720,120 C755,110 795,105 830,115 C860,125 890,140 920,170" />
            {/* Great Lakes hints */}
            <path d="M540,225 C580,205 630,210 665,232 C685,247 700,265 685,285" />
            <path d="M615,250 C640,255 660,262 665,275" strokeOpacity="0.22" />
            {/* Mexico stub */}
            <path d="M310,475 L360,490 L410,478" />
            {/* Inner subtle state grid */}
            <path d="M260,180 L720,180" strokeOpacity="0.12" />
            <path d="M260,260 L790,260" strokeOpacity="0.12" />
            <path d="M240,340 L780,340" strokeOpacity="0.12" />
            <path d="M340,180 L340,440" strokeOpacity="0.12" />
            <path d="M500,180 L500,455" strokeOpacity="0.12" />
            <path d="M660,180 L660,455" strokeOpacity="0.12" />
          </g>

          {/* Hub glow */}
          <circle cx={HUB.x} cy={HUB.y} r="60" fill="url(#hubGlow)" />

          {/* Connection lines: HUB → each distributor */}
          {DISTRIBUTORS.map((d, i) => {
            const c = COORDS[d.id];
            if (!c) return null;
            const stroke = colorForScore(d.score);
            return (
              <g key={`link-${d.id}`}>
                <line x1={HUB.x} y1={HUB.y} x2={c.x} y2={c.y} stroke={stroke} strokeOpacity="0.35" strokeWidth="1.4" />
                <line x1={HUB.x} y1={HUB.y} x2={c.x} y2={c.y} stroke={stroke} strokeWidth="1.6" strokeDasharray="3 6" className="animate-dash" style={{ animationDelay: `${i * 0.25}s` }} />
              </g>
            );
          })}

          {/* Hub */}
          <g>
            <circle cx={HUB.x} cy={HUB.y} r="7" fill="#00E5FF" />
            <circle cx={HUB.x} cy={HUB.y} r="12" fill="none" stroke="#00E5FF" strokeOpacity="0.5">
              <animate attributeName="r" from="8" to="22" dur="2s" repeatCount="indefinite" />
              <animate attributeName="stroke-opacity" from="0.7" to="0" dur="2s" repeatCount="indefinite" />
            </circle>
            <text x={HUB.x + 12} y={HUB.y - 10} fill="#E2E8F0" fontSize="11" fontFamily="JetBrains Mono">{HUB.name}</text>
          </g>

          {/* Distributor nodes */}
          {DISTRIBUTORS.map((d) => {
            const c = COORDS[d.id];
            if (!c) return null;
            const stroke = colorForScore(d.score);
            const isHover = hover === d.id;
            return (
              <g key={d.id} onMouseEnter={() => setHover(d.id)} onMouseLeave={() => setHover(null)} onClick={() => onSelect && onSelect(d)} style={{ cursor: "pointer" }} data-testid={`net-${d.id}`}>
                <circle cx={c.x} cy={c.y} r="24" fill="transparent" pointerEvents="all" />
                <circle cx={c.x} cy={c.y} r={isHover ? 12 : 8} fill={stroke} fillOpacity={isHover ? 0.9 : 0.75} pointerEvents="none" />
                <circle cx={c.x} cy={c.y} r={isHover ? 18 : 13} fill="none" stroke={stroke} strokeOpacity="0.4" pointerEvents="none" />
                <text x={c.x + 14} y={c.y - 6} fill="#E2E8F0" fontSize="11" fontFamily="Manrope" fontWeight="600" pointerEvents="none">{d.name.split(" ")[0]}</text>
                <text x={c.x + 14} y={c.y + 8} fill="#94A3B8" fontSize="10" fontFamily="JetBrains Mono" pointerEvents="none">{d.region} · {d.score}</text>
              </g>
            );
          })}
        </svg>

        {/* Info panel */}
        {hover && (() => {
          const d = DISTRIBUTORS.find((x) => x.id === hover);
          const c = COORDS[d.id];
          if (!d || !c) return null;
          return (
            <div className="absolute bottom-4 left-4 max-w-xs rounded-lg bg-[#0F1424]/95 border border-white/10 p-3 backdrop-blur-md" data-testid="net-hover-card">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: colorForScore(d.score) }} />
                <span className="text-[10px] font-mono text-slate-400">{d.id} · {c.city}</span>
              </div>
              <div className="font-heading text-white mt-1 text-sm">{d.name}</div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                <div><div className="tech-label text-[9px]">SCORE</div><div className="text-sm font-mono text-white">{d.score}</div></div>
                <div><div className="tech-label text-[9px]">FILL</div><div className={`text-sm font-mono ${d.fill < 90 ? "text-rose-300" : "text-emerald-300"}`}>{d.fill}%</div></div>
                <div><div className="tech-label text-[9px]">COVER</div><div className={`text-sm font-mono ${d.cover < 8 ? "text-rose-300" : "text-cyan-300"}`}>{d.cover}d</div></div>
              </div>
              <div className="mt-2 text-[10px] text-slate-400">Credit · <span className={d.credit === "Red" ? "text-rose-300" : d.credit === "Amber" ? "text-amber-300" : "text-emerald-300"}>{d.credit}</span></div>
            </div>
          );
        })()}
      </div>

      <div className="mt-3 flex items-center justify-between text-[10px] font-mono text-slate-500">
        <span>{DISTRIBUTORS.length} distributors · 24-month rolling window</span>
        <span>SYNC · ACTIVE · 2026-02-10 06:14 UTC</span>
      </div>
    </div>
  );
}
